import React, {
  useEffect,
  useMemo,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import ReferringProvider from 'pages/Dashboard/components/ReferringProvider';
import CompactContactEditor from 'pages/Dashboard/components/ReferringProvider/CompactContactEditor';
import {
  getProviderEntryConfigTarget,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfigTarget';
import getProviderSeenDatePairError from 'pages/Dashboard/components/ReferringProvider/providerContextDates';
import {
  NPISearchItemDTO,
  ProviderContextDTO,
  ProviderEntryDTO,
} from 'dtos';
import mapNPISearchItemToProviderEntry from 'pages/Dashboard/utils/providers';

type Part = 'provider' | 'contacts' | 'date' | 'dateContainer';

type Props = {
  classes?: Partial<Record<Part, string>>;
  required?: boolean;
  shouldStackContact?: boolean;
};

type ProviderPrimaryCarePhysician = Omit<ProviderContextDTO, 'providerEntry'> & {
  providerEntry: ProviderEntryDTO | null;
};

export default function PrimaryCarePhysicianInput({
  classes,
  required: requiredOverride,
  shouldStackContact,
}: Props) {
  const {
    control,
    trigger,
    setValue,
    clearErrors,
    watch,
  } = useFormContext();
  const primaryCarePhysician = watch('primaryCarePhysician') as ProviderContextDTO | undefined;
  const hasNoPrimaryCarePhysician = Boolean(watch('hasNoPrimaryCarePhysician'));
  const providerEntry = hasNoPrimaryCarePhysician ? null : primaryCarePhysician?.providerEntry;
  const storedProviderEntry = primaryCarePhysician?.providerEntry;
  const firstSeenOn = primaryCarePhysician?.firstSeenOn;
  const lastSeenOn = primaryCarePhysician?.lastSeenOn;
  const hasProviderSelected = !isNil(providerEntry);
  const hasStoredProvider = !isNil(storedProviderEntry);
  const providerEntryConfigTarget = hasStoredProvider
    ? getProviderEntryConfigTarget(storedProviderEntry)
    : null;

  const required = useMemo<boolean>(
    () => !hasNoPrimaryCarePhysician && (requiredOverride ?? hasProviderSelected),
    [hasNoPrimaryCarePhysician, hasProviderSelected, requiredOverride],
  );

  const updatePrimaryCarePhysician = (updates: Partial<ProviderPrimaryCarePhysician>) => {
    setValue('primaryCarePhysician', {
      ...(primaryCarePhysician ?? {}),
      ...updates,
    }, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (hasProviderSelected) {
      trigger(['primaryCarePhysician.firstSeenOn', 'primaryCarePhysician.lastSeenOn']);
    }
  }, [firstSeenOn, hasProviderSelected, lastSeenOn, trigger]);

  return (
    <div className="flex w-full flex-col flex-wrap gap-form-row">
      <Checkbox
        id="has-no-primary-care-physician"
        label="Patient does not have a Primary Care Physician"
        containerClassName="-mb-3"
        checked={hasNoPrimaryCarePhysician}
        onChange={(checked) => {
          setValue('hasNoPrimaryCarePhysician', checked, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          if (checked) {
            clearErrors([
              'primaryCarePhysician.providerEntry',
              'primaryCarePhysician.firstSeenOn',
              'primaryCarePhysician.lastSeenOn',
            ]);
          }
        }}
      />
      <Controller
        rules={{ required }}
        name="primaryCarePhysician.providerEntry"
        control={control}
        render={({ fieldState: { error } }) => (
          <Wrapper
            id="pcp-provider"
            label={hasProviderSelected ? 'Provider' : undefined}
            className={clsx(classes?.provider, {
              'max-w-[22rem] flex-1': hasProviderSelected,
            })}
            error={!isNil(error)}
            showErrorText={false}
          >
            <ReferringProvider
              value={providerEntry}
              disabled={hasNoPrimaryCarePhysician}
              onChange={(value?: NPISearchItemDTO | null) => {
                const nextProviderEntry = mapNPISearchItemToProviderEntry(value);
                updatePrimaryCarePhysician({
                  providerEntry: nextProviderEntry,
                });
              }}
              text="Primary Care Physician"
              showContactButton={false}
              variant="button"
              classes={{
                valueContainer: '!text-left !items-start flex-col !gap-0',
                name: 'font-semibold',
                button: '!h-12 flex-1',
                npiInfo: 'font-semibold text-[10px]',
              }}
            />
          </Wrapper>
        )}
      />
      {hasStoredProvider && (
        <div
          className={clsx(
            'grid overflow-hidden transition-[grid-template-rows,margin,opacity,transform] duration-200 ease-out motion-reduce:transition-none',
            {
              'grid-rows-[1fr] -mt-3 opacity-100 translate-y-0': !hasNoPrimaryCarePhysician,
              'grid-rows-[0fr] -mt-6 -translate-y-1 opacity-0 pointer-events-none': hasNoPrimaryCarePhysician,
            },
          )}
          aria-hidden={hasNoPrimaryCarePhysician}
        >
          <div className="min-h-0">
            <div className={clsx('flex w-1/2 gap-form-field', classes?.dateContainer)}>
              <Controller
                name="primaryCarePhysician.firstSeenOn"
                control={control}
                rules={{
                  validate: () => getProviderSeenDatePairError(firstSeenOn, lastSeenOn) ?? true,
                }}
                render={({ field, fieldState: { error } }) => (
                  <Wrapper
                    id="date-of-first-seen"
                    label="Date First Seen"
                    className={clsx('flex-1', classes?.date)}
                    showErrorText={false}
                    error={!isNil(error)}
                  >
                    <DatePicker
                      {...field}
                      containerClassName="!w-full"
                      inputContainerClassName="!w-full"
                      className="font-medium !w-full"
                      showDateButtons={false}
                      disableFuture
                      id="picker-for-date-of-first-seen"
                    />
                  </Wrapper>
                )}
              />
              <Controller
                name="primaryCarePhysician.lastSeenOn"
                control={control}
                rules={{
                  validate: () => getProviderSeenDatePairError(firstSeenOn, lastSeenOn) ?? true,
                }}
                render={({ field, fieldState: { error } }) => (
                  <Wrapper
                    id="date-of-last-seen"
                    label="Date Last Seen"
                    className={clsx('flex-1', classes?.date)}
                    showErrorText={false}
                    error={!isNil(error)}
                  >
                    <DatePicker
                      {...field}
                      containerClassName="!w-full"
                      inputContainerClassName="!w-full"
                      className="font-medium !w-full"
                      showDateButtons={false}
                      disableFuture
                      id="picker-for-date-of-last-seen"
                    />
                  </Wrapper>
                )}
              />
            </div>
            {!isNil(providerEntryConfigTarget) && (
              <div className="mt-3">
                <CompactContactEditor
                  target={providerEntryConfigTarget}
                  className={clsx('min-w-[14rem]', classes?.contacts)}
                  shouldStackContact={shouldStackContact}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
